import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, DialogActions, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  CreateVendorInput,
  useCreateVendorMutation,
  useGetNextVendorIdQuery,
  useUpdateVendorMutation
} from '../../../generated'
import { CANCEL, US_STATES } from '../../constants'
import { VendorModalProps } from '../../interfaceTypes/props'
import { createVendorSchema } from '../../validationSchema'
import { Alert } from '../common/Alert'
import CheckboxController from '../common/CheckboxController'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
import CustomPhoneController from '../common/CustomPhoneController'
import VendorAutoComplete from './VendorAutoComplete'
import OfficeAutoComplete from '../offices/OfficeAutocomplete'
import AccountDropdown from '../common/AccountDropdown'
import { AccountOption, AutoCompleteOption } from '../../interfaceTypes/common'

interface VendorFormValues extends Omit<CreateVendorInput, 'parentVendor' | 'officeId' | 'glAccountId'> {
  parentVendor: AutoCompleteOption
  office: AutoCompleteOption
  glAccount: AccountOption
  /** Display-only preview when creating; set from getNextVendorId query */
  vendorId?: string
}
const VenderModal = ({ vendor, isOpen, handleClose, refetch }: VendorModalProps) => {

  const { data: nextVendorIdData } = useGetNextVendorIdQuery({
    skip: !!vendor?.id || !isOpen,
    fetchPolicy: 'network-only'
  })

  const nextVendorId = nextVendorIdData?.getNextVendorId?.nextVendorId

  const methods = useForm<VendorFormValues>({
    resolver: yupResolver(createVendorSchema),
    defaultValues: vendor
      ? {
          ...vendor,
          addresses: vendor.addresses?.map(addr => addr || undefined) || [],
          parentVendor: vendor.parentVendor
            ? {
                label: vendor.parentVendor.name,
                value: vendor.parentVendor.id
              }
            : undefined,
          glAccount: vendor.glAccount
            ? {
                label: vendor.glAccount.name,
                value: vendor.glAccount.id
              }
            : undefined,
          office: vendor.office
            ? {
                label: vendor.office.name,
                value: vendor.office.id
              }
            : undefined
        }
      : undefined
  })

  const { handleSubmit, reset, control, setValue } = methods

  useEffect(() => {
    if (!vendor?.id && isOpen) {
      setValue('vendorId', nextVendorId ?? 'Loading...')
    }
  }, [nextVendorId, vendor?.id, isOpen, setValue])

  const [createVendor, { loading }] = useCreateVendorMutation({
    onError(error) {
      if (error.message === 'Not Found Exception') Alert.error('Invalid Phone')
      else Alert.error(error.message)
    },
    onCompleted(data) {
      Alert.success('Vendor Created')
      console.log('created vendor', data)
      handleClose()
      refetch?.()
      reset()
    }
  })

  const [updateVendor, { loading: updating }] = useUpdateVendorMutation({
    onError(error) {
      if (error.message === 'Not Found Exception') Alert.error('Invalid Phone')
      else Alert.error(error.message)
    },
    onCompleted(data) {
      Alert.success('Vendor Updated')
      console.log('updated vendor', data)
      handleClose()
      refetch?.()
      reset()
    }
  })

  const onSubmit = async (data: VendorFormValues) => {
    console.log('data', data)

    const {
      vendorId,
      parentVendor,
      separateCheckPerInvoice,
      name,
      required1099,
      accountNumber,
      addresses,
      description,
      email,
      glAccount,
      phone,
      socialSecurityNumber,
      sortCode,
      taxId,
      office
    } = data

    const addressInput = addresses?.map(addr => ({
      state: addr.state,
      city: addr.city,
      zipCode: addr.zipCode,
      country: addr.country,
      address1: addr.address1,
      address2: addr.address2
    }))
    const input = {
      officeId: office?.value,
      vendorId,
      parentVendor: !!parentVendor.label ? parentVendor?.value : undefined,
      separateCheckPerInvoice: separateCheckPerInvoice ?? false,
      name,
      required1099: required1099 ?? false,
      accountNumber,
      addresses: addressInput,
      description,
      email,
      glAccountId: !!glAccount ? glAccount.value : null,
      phone,
      socialSecurityNumber: !!socialSecurityNumber ? socialSecurityNumber : undefined,
      sortCode: sortCode?.toLocaleUpperCase(),
      taxId
    }
    vendor?.id
      ? await updateVendor({
          variables: {
            updateVendorInput: { ...input, id: vendor.id, addressId: vendor?.addresses?.[0]?.id }
          }
        })
      : await createVendor({
          variables: {
            createVendorInput: input
          }
        })
  }

  return (
    <CustomModal title="Vendor Details" isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} rowSpacing={2}>
            <Grid item md={3} sm={12} xs={12}>
              <CustomController controllerLabel="Vendor ID" controllerName="vendorId" />
            </Grid>
            <Grid item md={5} sm={12} xs={12}>
              <CustomController controllerLabel="Name/Business Name" controllerName="name" />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <CustomPhoneController controllerLabel="Phone" controllerName="phone" />
            </Grid>
            <Grid item md={3} sm={12} xs={12}>
              <CustomController upperCase controllerLabel="Sort Code" controllerName="sortCode" fieldType="text" />
            </Grid>
            <Grid item md={5} sm={12} xs={12}>
              <CustomController controllerLabel="Email" controllerName="email" fieldType="text" />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerLabel="SSN" controllerName="socialSecurityNumber" />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerLabel="Tax ID" controllerName="taxId" />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerLabel="Account No" controllerName="accountNumber" />
            </Grid>
            <Grid item md={4} sm={12} xs={12} mt={1}>
              <FormControl variant="outlined" fullWidth>
                <Controller
                  name="glAccount"
                  control={control}
                  render={({ field }) => (
                    <AccountDropdown
                      value={field.value}
                      onChange={field.onChange}
                      label={'GL Account'}
                      searchFilters={{
                        isBankAccount: false
                      }}
                      sx={{}}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <VendorAutoComplete
                name="parentVendor"
                label={'Parent Vendor'}
                currentVendor={vendor}
                parent={vendor?.parentVendor ?? undefined}
              />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <OfficeAutoComplete
                name="office"
                label="Profit Center"
                currentOffice={!!vendor?.office?.id ? vendor.office : undefined}
                autoSelectOnTab
              />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address1"
                controllerLabel="Address line 1"
                fieldType="text"
              />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address2"
                controllerLabel="Address line 2"
                fieldType="text"
              />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerName="addresses[0].city" controllerLabel="City" fieldType="text" />
            </Grid>
            <Grid item md={4} sm={12} xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                <InputLabel id="address-state">State</InputLabel>

                <Controller
                  name="addresses.0.state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="address-state"
                      label="State"
                      fullWidth
                      MenuProps={{ style: { maxHeight: 300 } }}
                    >
                      {US_STATES.map((item, index) => {
                        const { name } = item || {}
                        return (
                          <MenuItem value={name} key={`${item}-${index}`}>
                            {name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController controllerName="addresses.0.zipCode" controllerLabel="Zip Code" fieldType="text" />
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].country"
                controllerLabel="Country"
                fieldType="text"
                defaultValue="USA"
                readOnly
              />
            </Grid>
            <Grid item md={7} sm={12} xs={12}>
              <CustomController controllerLabel="Description" controllerName="description" isMultiLine rowsLength={2} />
            </Grid>
            <Grid item md={5} sm={12} xs={12} alignItems={'center'}>
              <CheckboxController label="Required 1099" name="required1099" />
              <CheckboxController label="Separate Check Per Invoice" name="separateCheckPerInvoice" />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || updating}
              aria-label="add new client"
              endIcon={(loading || updating) && <CircularProgress size={20} color="inherit" />}
            >
              {vendor?.id ? 'Update' : 'Add'} Vendor
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default VenderModal
